/**
 * Real-Time Digital Filtering Service
 * Phase 1: Essential Clinical Features
 *
 * Implements Butterworth filters for real-time EEG signal processing:
 * - High-pass filter (removes DC drift and slow artifacts)
 * - Low-pass filter (removes high-frequency noise)
 * - Notch filter (removes power line interference at 50/60 Hz)
 *
 * Compliant with ACNS Guideline 1 filtering recommendations.
 */
import logger from "@shared/utils/logger";

export interface FilterParameters {
  highPass: number; // Hz (0.3 - 1.0 Hz typical)
  lowPass: number; // Hz (35 - 70 Hz typical)
  notch: number | null; // Hz (50 or 60 Hz, null to disable)
  samplingRate: number; // Hz
  order?: number; // Filter order (default: 4)
}

export interface FilteredSignalResult {
  filteredData: number[][];
  appliedFilters: FilterParameters;
  processingTime: number; // milliseconds
}

/**
 * Butterworth filter coefficients calculator
 */
class ButterworthFilter {
  private a: number[] = [];
  private b: number[] = [];

  constructor(
    cutoffFreq: number,
    samplingRate: number,
    filterType: "highpass" | "lowpass" | "bandstop",
    order: number = 4,
    notchBandwidth?: number,
  ) {
    this.calculateCoefficients(
      cutoffFreq,
      samplingRate,
      filterType,
      order,
      notchBandwidth,
    );
  }

  private calculateCoefficients(
    fc: number | [number, number],
    fs: number,
    type: string,
    order: number,
    bandwidth?: number,
  ): void {
    // Handle bandpass as combination of highpass and lowpass
    if (type === "bandpass" && Array.isArray(fc)) {
      const [lowFreq, highFreq] = fc;
      // Design as cascade of highpass and lowpass filters
      // For now, use simplified 2nd order approach
      const wc1 = (2 * Math.PI * lowFreq) / fs;
      const wc2 = (2 * Math.PI * highFreq) / fs;

      // Use center frequency and bandwidth for bandpass
      const centerFreq = Math.sqrt(lowFreq * highFreq);
      const bw = highFreq - lowFreq;
      const wc = (2 * Math.PI * centerFreq) / fs;
      const wbw = (2 * Math.PI * bw) / fs;

      // 2nd order Butterworth bandpass
      const Q = centerFreq / bw;
      const K = Math.tan(wc / 2);
      const norm = 1 / (1 + K / Q + K * K);

      this.b = [(K / Q) * norm, 0, (-K / Q) * norm];
      this.a = [1, 2 * (K * K - 1) * norm, (1 - K / Q + K * K) * norm];
      return;
    }

    // For single cutoff frequency filters
    if (typeof fc !== "number") {
      throw new Error(
        "Single cutoff frequency required for non-bandpass filters",
      );
    }

    // Normalized frequency (0 to 1, where 1 is Nyquist)
    const wc = (2 * Math.PI * fc) / fs;

    if (type === "highpass") {
      // 2nd order Butterworth high-pass (IIR)
      const K = Math.tan(wc / 2);
      const norm = 1 / (1 + Math.sqrt(2) * K + K * K);

      this.b = [norm, -2 * norm, norm];
      this.a = [
        1,
        2 * (K * K - 1) * norm,
        (1 - Math.sqrt(2) * K + K * K) * norm,
      ];
    } else if (type === "lowpass") {
      // 2nd order Butterworth low-pass (IIR)
      const K = Math.tan(wc / 2);
      const norm = 1 / (1 + Math.sqrt(2) * K + K * K);

      this.b = [K * K * norm, 2 * K * K * norm, K * K * norm];
      this.a = [
        1,
        2 * (K * K - 1) * norm,
        (1 - Math.sqrt(2) * K + K * K) * norm,
      ];
    } else if (type === "bandstop" && bandwidth) {
      // Notch filter (band-stop)
      const bw = (2 * Math.PI * bandwidth) / fs;
      const r = 1 - (3 * bw) / 2;
      const cosWc = Math.cos(wc);
      const K = (1 - 2 * r * cosWc + r * r) / (2 - 2 * cosWc);

      this.b = [K, -2 * K * cosWc, K];
      this.a = [1, -2 * r * cosWc, r * r];
    }
  }

  /**
   * Apply filter to signal using Direct Form II Transposed
   */
  public apply(signal: number[]): number[] {
    const filtered = new Array(signal.length);
    const x = [0, 0]; // Input delay line
    const y = [0, 0]; // Output delay line

    for (let i = 0; i < signal.length; i++) {
      // Direct Form II Transposed implementation
      filtered[i] = this.b[0] * signal[i] + x[0];
      x[0] = this.b[1] * signal[i] - this.a[1] * filtered[i] + x[1];
      x[1] = this.b[2] * signal[i] - this.a[2] * filtered[i];
    }

    return filtered;
  }
}

/**
 * SignalFilteringService - Main service for applying filters to EEG data
 */
export class SignalFilteringService {
  /**
   * Apply real-time digital filters to multi-channel EEG data
   *
   * @param data - 2D array [channels][samples]
   * @param params - Filter parameters
   * @returns Filtered data and metadata
   */
  public static applyFilters(
    data: number[][],
    params: FilterParameters,
  ): FilteredSignalResult {
    const startTime = performance.now();

    if (!data || data.length === 0 || data[0].length === 0) {
      throw new Error("Invalid input data: empty or malformed");
    }

    // Validate parameters
    this.validateParameters(params);

    // Apply filters to each channel
    const filteredData = data.map((channelData) => {
      let filtered = [...channelData];

      // Apply high-pass filter (remove DC drift and slow artifacts)
      if (params.highPass > 0) {
        const hpFilter = new ButterworthFilter(
          params.highPass,
          params.samplingRate,
          "highpass",
          params.order || 4,
        );
        filtered = hpFilter.apply(filtered);
      }

      // Apply low-pass filter (remove high-frequency noise)
      if (params.lowPass < params.samplingRate / 2) {
        const lpFilter = new ButterworthFilter(
          params.lowPass,
          params.samplingRate,
          "lowpass",
          params.order || 4,
        );
        filtered = lpFilter.apply(filtered);
      }

      // Apply notch filter (remove power line interference)
      if (params.notch !== null) {
        const notchFilter = new ButterworthFilter(
          params.notch,
          params.samplingRate,
          "bandstop",
          2, // 2nd order for notch
          2.0, // 2 Hz bandwidth
        );
        filtered = notchFilter.apply(filtered);
      }

      return filtered;
    });

    const processingTime = performance.now() - startTime;

    return {
      filteredData,
      appliedFilters: params,
      processingTime,
    };
  }

  /**
   * Validate filter parameters against clinical standards
   */
  private static validateParameters(params: FilterParameters): void {
    const nyquist = params.samplingRate / 2;

    if (params.highPass < 0 || params.highPass >= nyquist) {
      throw new Error(
        `High-pass frequency must be between 0 and ${nyquist} Hz`,
      );
    }

    if (params.lowPass <= 0 || params.lowPass > nyquist) {
      throw new Error(`Low-pass frequency must be between 0 and ${nyquist} Hz`);
    }

    if (params.highPass >= params.lowPass) {
      throw new Error(
        "High-pass frequency must be less than low-pass frequency",
      );
    }

    if (params.notch !== null) {
      if (params.notch !== 50 && params.notch !== 60) {
        logger.warn("Notch filter frequency should typically be 50 or 60 Hz");
      }
    }
  }

  /**
   * Get ACNS-recommended filter settings for clinical EEG
   */
  public static getACNSDefaults(samplingRate: number): FilterParameters {
    return {
      highPass: 0.3, // ACNS Guideline 1
      lowPass: 35, // ACNS Guideline 1 (conservative for clinical use)
      notch: 60, // US standard (use 50 for Europe/Asia)
      samplingRate,
      order: 4,
    };
  }

  /**
   * Get research-grade filter settings (wider bandwidth)
   */
  public static getResearchDefaults(samplingRate: number): FilterParameters {
    return {
      highPass: 1.0,
      lowPass: 70,
      notch: 60,
      samplingRate,
      order: 4,
    };
  }

  /**
   * Apply Butterworth bandpass filter to signal
   * Used for HFO detection and other frequency-specific analysis
   *
   * @param signal - Input signal
   * @param lowFreq - Low cutoff frequency (Hz)
   * @param highFreq - High cutoff frequency (Hz)
   * @param samplingRate - Sampling rate (Hz)
   * @param order - Filter order (default: 4)
   * @returns Filtered signal
   */
  public static applyBandpassFilter(
    signal: number[],
    lowFreq: number,
    highFreq: number,
    samplingRate: number,
    order: number = 4,
  ): number[] {
    if (lowFreq >= highFreq) {
      throw new Error("Low frequency must be less than high frequency");
    }

    const nyquist = samplingRate / 2;
    if (highFreq >= nyquist) {
      throw new Error(
        `High frequency (${highFreq} Hz) must be less than Nyquist frequency (${nyquist} Hz)`,
      );
    }

    // Implement bandpass as cascade: highpass then lowpass
    // This is more robust than direct bandpass design
    const highpassFilter = new ButterworthFilter(
      lowFreq,
      samplingRate,
      "highpass",
      2, // 2nd order
    );

    const lowpassFilter = new ButterworthFilter(
      highFreq,
      samplingRate,
      "lowpass",
      2, // 2nd order
    );

    // Apply filters in sequence
    const afterHighpass = highpassFilter.apply(signal);
    const afterLowpass = lowpassFilter.apply(afterHighpass);

    return afterLowpass;
  }
}

export default SignalFilteringService;
